import React from 'react';
import { Card } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { Button } from '../../../../components/ui/Button/Button';
import { Clock, Calendar, Mail, Phone, MessageSquare } from 'lucide-react';

interface CampaignSettingsProps {
  data: any;
  onChange: (field: string, value: any) => void;
  errors: Record<string, string>;
}

export const CampaignSettings: React.FC<CampaignSettingsProps> = ({ data, onChange, errors }) => {
  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 mb-2">
          Campaign Settings
        </h2>
        <p className="text-neutral-600 dark:text-neutral-400">
          Configure timing, limits, and behavior settings
        </p>
      </div>

      {/* Timing Settings */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
          Timing & Schedule
        </h3>
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Send Time Zone
            </label>
            <select className="w-full px-3 py-2 border border-neutral-200 dark:border-neutral-700 rounded-md bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100">
              <option value="recipient">Recipient's timezone</option>
              <option value="company">Company timezone</option>
              <option value="utc">UTC</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Sending Days
            </label>
            <div className="flex flex-wrap gap-2">
              {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day) => (
                <button
                  key={day}
                  className="px-3 py-1 text-sm border border-neutral-200 dark:border-neutral-700 rounded-md hover:bg-neutral-50 dark:hover:bg-neutral-800"
                >
                  {day}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Send Time Window
            </label>
            <div className="flex items-center space-x-2">
              <Input type="time" defaultValue="09:00" className="w-24" />
              <span className="text-neutral-600 dark:text-neutral-400">to</span>
              <Input type="time" defaultValue="17:00" className="w-24" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Daily Send Limit
            </label>
            <Input type="number" placeholder="50" />
          </div>
        </div>
      </Card>

      {/* Email Settings */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
          Email Settings
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-neutral-900 dark:text-neutral-100">Track Opens</h4>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">Monitor when emails are opened</p>
            </div>
            <input
              type="checkbox"
              defaultChecked
              className="w-4 h-4 text-blue-600 bg-neutral-100 border-neutral-300 rounded focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-neutral-900 dark:text-neutral-100">Track Clicks</h4>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">Monitor link clicks in emails</p>
            </div>
            <input
              type="checkbox"
              defaultChecked
              className="w-4 h-4 text-blue-600 bg-neutral-100 border-neutral-300 rounded focus:ring-blue-500"
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-neutral-900 dark:text-neutral-100">Auto-stop on Reply</h4>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">Stop sequence when prospect replies</p>
            </div>
            <input
              type="checkbox"
              defaultChecked
              className="w-4 h-4 text-blue-600 bg-neutral-100 border-neutral-300 rounded focus:ring-blue-500"
            />
          </div>
        </div>
      </Card>

      {/* Follow-up Settings */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
          Follow-up Behavior
        </h3>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Follow-up after no response
            </label>
            <div className="flex items-center space-x-2">
              <Input type="number" placeholder="3" className="w-20" />
              <span className="text-neutral-600 dark:text-neutral-400">days</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              Maximum follow-ups
            </label>
            <Input type="number" placeholder="5" className="w-20" />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-medium text-neutral-900 dark:text-neutral-100">Skip weekends</h4>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">Don't send emails on Saturday and Sunday</p>
            </div>
            <input
              type="checkbox"
              defaultChecked
              className="w-4 h-4 text-blue-600 bg-neutral-100 border-neutral-300 rounded focus:ring-blue-500"
            />
          </div>
        </div>
      </Card>

      {/* Integration Settings */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-4">
          Integrations
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-2">
              <Mail className="w-5 h-5 text-blue-500" />
              <h4 className="font-medium text-neutral-900 dark:text-neutral-100">Email Provider</h4>
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">Gmail connected</p>
            <Button variant="outline" size="sm">Configure</Button>
          </div>
          <div className="border border-neutral-200 dark:border-neutral-700 rounded-lg p-4">
            <div className="flex items-center space-x-3 mb-2">
              <Phone className="w-5 h-5 text-green-500" />
              <h4 className="font-medium text-neutral-900 dark:text-neutral-100">CRM Integration</h4>
            </div>
            <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-2">Not connected</p>
            <Button variant="outline" size="sm">Connect</Button>
          </div>
        </div>
      </Card>
    </div>
  );
}; 